/**
 * 연령 확인 게이트 (오픈이슈 OI-43 · 2026-09-14 사용자 결정).
 *
 * **왜 생겼나**: 2026-08-04 소셜 전용 재편에서 본인인증(NICE)을 걷어내면서 만 14세
 * 확인이 함께 사라졌다. 국내 개인정보보호법은 만 14세 미만의 개인정보 수집에
 * 법정대리인 동의를 요구하고, 미국 배포는 COPPA 의 중립적 연령 확인(neutral age
 * screening)을 요구한다 — 포켓몬 소재라 "아동 대상" 판정 요소가 강하다.
 *
 * **언제 묻는가**: **가입할 때만** — 소셜 인증을 마쳐 "처음 보는 계정"(`NEED_AGE`)임이
 * 드러난 순간이다(2026-09-15 재배치). 종전에는 소셜 버튼을 누르는 즉시, 즉 인증 전에
 * 물었다. 그때는 서버가 처음 보는 계정을 그 자리에서 만들어 버려 물을 자리가 뒤에
 * 없었기 때문인데, 그 대가로 **이미 가입한 사람까지 로그인할 때마다** 생년월일을
 * 요구받았다. 지금은 서버가 신규 계정에 회원 대신 가입 대기표를 내주므로(`api/social`)
 * 인증과 가입 사이에 물을 자리가 있다.
 *
 * **판정은 여기(로컬)와 서버 양쪽에서 한다.** 로컬은 미달이면 서버를 부르지 않고 화면에서
 * 끊고, 서버는 가입 요청의 생년월일을 다시 검증해 저장한다(`BIRTH_YMD` — 종전
 * `19000101` placeholder 자리). 클라이언트 판정만 믿으면 API 를 직접 부르는 경로로
 * 확인 없는 계정이 생긴다.
 *
 * ⚠ **판정을 저장하지 않는다**(2026-09-15 사용자 결정). 미달이어도 다시 물어보는 쪽을
 *   택했다 — 오타 한 번으로 그 기기에서 영영 가입이 막히는 편이 더 나쁘다고 봤다.
 *   되돌아가 나이를 고쳐 넣는 우회는 이 게이트가 막지 못한다(막으려면 본인인증이다).
 */

/** 가입 가능 최소 연령 (만 나이). */
export const MIN_AGE = 14;

/**
 * 입력 문자열을 YYYYMMDD 로 정규화한다. 형식·실존·범위가 어긋나면 null.
 *
 * 숫자 외 문자는 버린다 — 사용자가 `2012-03-15` 나 `2012.03.15` 로 치는 것이 자연스럽고,
 * 그걸 오류로 되돌려주는 것은 입력을 방해할 뿐이다.
 */
export function parseBirthYmd(raw: string, now: Date = new Date()): string | null {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length !== 8) return null;

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  // 실존 날짜인지 — Date 는 2월 31일을 3월 3일로 조용히 넘긴다.
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  if (d.getTime() > now.getTime()) return null; // 미래

  return digits;
}

/** 만 나이 — 생일이 아직 안 지났으면 한 살 적다. */
export function ageOn(birthYmd: string, now: Date = new Date()): number {
  const year = Number(birthYmd.slice(0, 4));
  const month = Number(birthYmd.slice(4, 6));
  const day = Number(birthYmd.slice(6, 8));

  let age = now.getFullYear() - year;
  const beforeBirthday =
    now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age;
}

export function isEligible(birthYmd: string, now: Date = new Date()): boolean {
  return ageOn(birthYmd, now) >= MIN_AGE;
}
